import { Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, defer, Observable, of, throwError } from 'rxjs';
import {
  catchError,
  debounceTime,
  delay,
  distinctUntilChanged,
  exhaustMap,
  filter,
  finalize,
  map,
  mergeMap,
  retryWhen,
  shareReplay,
  switchMap,
  take,
  tap,
} from 'rxjs/operators';

// Generate unique idempotency key for order creation.
// WebView2 / non-secure contexts may lack crypto.randomUUID.
function generateIdempotencyKey(): string {
  const webCrypto = globalThis.crypto;
  if (typeof webCrypto?.randomUUID === 'function') {
    return webCrypto.randomUUID();
  }
  if (typeof webCrypto?.getRandomValues === 'function') {
    const bytes = new Uint8Array(16);
    webCrypto.getRandomValues(bytes);
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    const hex = Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
  }
  return `idem-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

import { DateUtils } from '@shared/utils/date/date.utils';
import { LocationUtils } from '@shared/utils/location/location.utils';

import { CartItemKeys, CartKeys } from '@config/keys/cart.keys';
import { MarketplaceKeys, ShopKeys } from '@config/keys/shop.keys';
import { UserKeys } from '@config/keys/user.keys';

import { CartStoreDispatcher } from '@common/dispatchers/cart.dispatcher';
import { OrderStoreDispatcher } from '@common/dispatchers/order.dispatcher';
import { ShopStoreDispatcher } from '@common/dispatchers/shop.dispatcher';
import { TransactionStoreDispatcher } from '@common/dispatchers/transaction.dispatcher';

import { AuthStoreSelector } from '@common/selectors/authentication.selector';
import { BasketStoreSelector } from '@common/selectors/basket.selector';
import { CartFunnelStoreSelector } from '@common/selectors/cart-funnel.selector';
import { CartStoreSelector } from '@common/selectors/cart.selector';
import { DeliveryStoreSelector } from '@common/selectors/delivery.selector';
import { MarketplaceStoreSelector } from '@common/selectors/marketplace.selector';
import { ShopStoreSelector } from '@common/selectors/shop.selector';

import { OrderStatusValues, OrderTypeValues } from '@config/values/order.values';

import {
  CartItemWithOrigin,
  DoodCartModel,
  ICartDeclinableAdditionsItem,
  ICartDeclinableSeparateAdditions,
  ICartItem,
  NOT_HANDLED_PRODUCT_ORIGIN,
} from '@core/models/cart.model';
import { DoodDeliveryAddressModel } from '@core/models/delivery-address.model';
import { GeocoderAddressComponent, PlaceResult } from '@core/models/google-maps.model';
import {
  ICartValidateItem,
  ICartValidateItemProduct,
  IOrderValidate,
  IValidateDeliveryAddress,
  IViolations,
} from '@core/models/order.model';
import { DoodTransactionModel } from '@core/models/transaction.model';
import { DoodUserModel } from '@store/authentication/authentication.model';
import {
  DoodAddressModel,
  DoodAppDevice,
  DoodAppPlatform,
  DoodCreateOrderQuery,
  DoodOrderModel,
  DoodOrderStatus,
  DoodOrderType,
  DoodOrderUserModel,
  DoodOrderViolationModel,
  DoodUsePointModel,
  DoodValidateOrderQuery,
  DoodValidateOrderResponse,
} from '@store/order/order.model';
import { DoodShopModel } from '@core/models/shop.model';

import { AuthFirebaseService } from '@core/services/api/auth-firebase/auth-firebase.service';
import { OrdersApiService } from '@core/services/api/orders/orders-api.service';
import { AnalyticsService } from '@core/services/app/analytics.service';
import { ErrorService } from '@core/services/error-service/error.service';
import { EventTrackerService } from '@core/services/event-tracker/event-tracker.service';
import { HiddenCommentStorageService } from '@core/services/hidden-comment-storage/hidden-comment-storage.service';
import { KioskService } from '@core/services/kiosk/kiosk.service';
import { ModalsService } from '@core/services/modals/modals.service';
import { OrderTypeService } from '@core/services/order-type/order-type.service';
import { ResetStateService } from '@core/services/reset-state.service';
import { UtmStorageService } from '@core/services/utm-storage/utm-storage.service';
import { LinkNavigationService } from '@core/services/link-navigation/link-navigation.service';
import { UserService } from '@core/services/user/user.service';
import {
  isDoodAnonymousJwt,
  isTokenExpired,
  peekJwtSubject,
} from '@core/helpers/dood-anonymous-jwt.helper';
import { logToJitsu } from '@shared/utils/logger/logger.utils';

import { OrderStoreSelector } from '@common/selectors/order.selector';
import { Paths } from '@config/paths.config';
import { TranslateService } from '@ngx-translate/core';
import { AlertModalComponent } from '@shared/modals/alert-modal/alert-modal.component';
import { CartParametersModalComponent } from '@shared/modals/cart-parameters-modal/cart-parameters-modal.component';
import type {
  UnavailableProductCard,
  UnavailableProductModalConfig,
} from '@shared/modals/unavailable-product-modal/unavailable-product-modal.component';
import { NotificationService } from '../notification/notification.service';

const DEFAULT_DELIVERY_DURATION_SECONDS = 900;
const VALIDATE_ORDER_CACHE_TTL_MS = 30_000;
const VALIDATE_ADDRESS_CACHE_TTL_MS = 60_000;

interface ValidateOrderCacheEntry {
  requestKey: string;
  cachedAt: number;
  response: DoodValidateOrderResponse;
}

interface ValidateAddressCacheEntry {
  requestKey: string;
  cachedAt: number;
  isValid: boolean;
}

type ComparableValidationProduct = {
  [CartItemKeys.Id]: string;
  [CartItemKeys.Quantity]?: number;
  [CartItemKeys.Products]?: ComparableValidationProduct[];
  [CartItemKeys.Categories]?: {
    products: ComparableValidationProduct[];
  }[];
};

type ComparableValidationInputProduct = {
  [CartItemKeys.Id]?: unknown;
  [CartItemKeys.Quantity]?: unknown;
  [CartItemKeys.Products]?: unknown;
  [CartItemKeys.Categories]?: unknown;
};

type ValidatedAdditionItem = {
  id?: unknown;
  addition_group?: unknown;
};

@Injectable({
  providedIn: 'root',
})
export class OrdersService {
  cart$ = this.cartSelector.selectActive$;
  checkCartIsValidCallInProgress$ = new BehaviorSubject<boolean>(false);
  private validateOrderCache: ValidateOrderCacheEntry | null = null;
  private validateOrderInFlightByKey = new Map<string, Observable<DoodValidateOrderResponse>>();
  private validateAddressCache: ValidateAddressCacheEntry | null = null;
  private validateAddressInFlightByKey = new Map<string, Observable<boolean>>();

  constructor(
    private cartSelector: CartStoreSelector,
    private authSelector: AuthStoreSelector,
    private shopSelector: ShopStoreSelector,
    private basketSelector: BasketStoreSelector,
    private cartDispatcher: CartStoreDispatcher,
    private shopDispatcher: ShopStoreDispatcher,
    private readonly kioskService: KioskService,
    private readonly modalsService: ModalsService,
    private orderDispatcher: OrderStoreDispatcher,
    private deliverySelector: DeliveryStoreSelector,
    private cartFunnelSelector: CartFunnelStoreSelector,
    private readonly orderTypeService: OrderTypeService,
    private readonly ordersApiService: OrdersApiService,
    private marketplaceSelector: MarketplaceStoreSelector,
    private transactionDispatcher: TransactionStoreDispatcher,
    private readonly eventTrackerService: EventTrackerService,
    protected readonly authFirebaseService: AuthFirebaseService,
    protected readonly analyticsService: AnalyticsService,
    protected readonly resetStateService: ResetStateService,
    private readonly orderStoreSelector: OrderStoreSelector,
    private readonly utmStorageService: UtmStorageService,
    private readonly hiddenCommentStorageService: HiddenCommentStorageService,
    private readonly notificationService: NotificationService,
    private readonly translateService: TranslateService,
    private readonly linkNavigationService: LinkNavigationService,
    private readonly userService: UserService,
  ) {}

  /**
   * Prefer `anonymous_access_token`; `anonymous_firebase_token` is a deprecated alias (same Dood JWT).
   */
  private applyAnonymousAccessTokenFromOrder(order: DoodOrderModel | null | undefined): void {
    const accessToken = order?.anonymous_access_token || order?.anonymous_firebase_token;
    if (!accessToken || !isDoodAnonymousJwt(accessToken)) {
      return;
    }
    const currentSubject = peekJwtSubject(this.authSelector.selectStatus().token);
    const orderSubject = peekJwtSubject(accessToken);
    if (currentSubject && orderSubject && currentSubject !== orderSubject) {
      // Diagnostic only: the session JWT is about to switch to another guest (DOOD-3495).
      logToJitsu(
        this.analyticsService,
        'warn',
        'order_access_token_subject_changed',
        '[Order] Anonymous access token from order belongs to another user',
        {
          orderId: order?.id,
          currentSubject,
          orderSubject,
          sessionUserId: this.authSelector.selectUser()?.id ?? null,
        },
      );
    }
    this.userService.setDoodAnonymousAccessToken(accessToken);
    this.authFirebaseService.startAnonymousUserLogoutTimer();
  }

  /**
   * `order.user` comes from the persisted cart, which can still hold a previous guest
   * after the Dood anonymous JWT was renewed. The API rejects that with a 403, so send
   * the session user instead (DOOD-3495).
   */
  private alignOrderUserWithSession(
    order: DoodCreateOrderQuery | null,
    user: DoodUserModel | null | undefined,
  ): DoodCreateOrderQuery | null {
    if (!order?.user || !user?.id || order.user === user.id) {
      return order;
    }
    logToJitsu(
      this.analyticsService,
      'warn',
      'order_user_mismatch',
      '[Order] Cart user differs from session user, using session user',
      {
        cartUserId: order.user,
        sessionUserId: user.id,
        jwtSubject: peekJwtSubject(this.authSelector.selectStatus().token),
      },
    );
    return { ...order, user: user.id };
  }

  /** Diagnostic for 401/403 on order creation: which identities were sent (DOOD-3495). */
  private logOrderAuthorizationError(error: unknown, order: DoodCreateOrderQuery): void {
    const httpError = error as { status?: number; error?: unknown } | null;
    if (httpError?.status !== 401 && httpError?.status !== 403) {
      return;
    }
    const status = this.authSelector.selectStatus();
    const body = httpError.error as { error?: string; title?: string; detail?: string } | null;
    logToJitsu(
      this.analyticsService,
      'error',
      'order_create_forbidden',
      '[Order] Order creation rejected by API',
      {
        status: httpError.status,
        apiError: body?.error ?? null,
        apiTitle: body?.title ?? null,
        apiDetail: body?.detail ?? null,
        orderUserId: order.user ?? null,
        sessionUserId: this.authSelector.selectUser()?.id ?? null,
        jwtSubject: peekJwtSubject(status.token),
        isDoodAnonymousJwt: isDoodAnonymousJwt(status.token),
        tokenExpired: isTokenExpired(status.tokenExpiresAt),
        kioskId: order.kiosk ?? null,
      },
    );
  }

  duplicateCartItem(items: CartItemWithOrigin[]): ICartValidateItem[] {
    const newArr: ICartValidateItem[] = [];
    items.map(item => {
      // Skip items with isServiceCharge when duplicating
      if ((item as any)[CartItemKeys.IsServiceCharge]) {
        return;
      }
      for (let i = 0; i < item[CartItemKeys.Quantity]; ++i) {
        if (item[CartItemKeys.Products]?.length) {
          newArr.push({
            [CartItemKeys.Id]: item[CartItemKeys.ItemId],
            [CartItemKeys.ShopId]: item[CartItemKeys.ShopId],
            [CartItemKeys.Products]: item[CartItemKeys.Products] as ICartValidateItemProduct[],
            [CartItemKeys.Amount]: item[CartItemKeys.Amount],
            [CartItemKeys.AcquisitionContext]: item[CartItemKeys.Origin],
          });
        } else {
          newArr.push({
            [CartItemKeys.Id]: item[CartItemKeys.ItemId],
            [CartItemKeys.ShopId]: item[CartItemKeys.ShopId],
            ...(item[CartItemKeys.Additions]?.length && {
              [CartItemKeys.Additions]: [...item[CartItemKeys.Additions]],
            }),
            [CartItemKeys.Amount]: item[CartItemKeys.Amount],
            [CartItemKeys.AcquisitionContext]: item[CartItemKeys.Origin],
          });
        }
      }
    });

    // Filter out any items that might have isServiceCharge set
    return newArr.filter(item => !item[CartItemKeys.IsServiceCharge]);
  }

  private mapValidatedProductsToCartItems(products: ICartValidateItem[]): CartItemWithOrigin[] {
    const cartItems = products
      .filter(product => !product[CartItemKeys.IsServiceCharge])
      .map((product, index) => {
        const childProducts = this.mapValidatedProductChildrenToCartItems(product);
        return {
          [CartItemKeys.Id]: index + 1,
          [CartItemKeys.ItemId]: product[CartItemKeys.Id],
          [CartItemKeys.ShopId]: product[CartItemKeys.ShopId],
          [CartItemKeys.Additions]: this.mapValidatedAdditionsToCartAdditions(
            product[CartItemKeys.Additions],
          ),
          [CartItemKeys.Quantity]: product[CartItemKeys.Quantity] ?? 1,
          [CartItemKeys.Origin]:
            product[CartItemKeys.AcquisitionContext] ?? NOT_HANDLED_PRODUCT_ORIGIN,
          ...(product[CartItemKeys.Amount] !== undefined && {
            [CartItemKeys.Amount]: product[CartItemKeys.Amount],
          }),
          ...(childProducts.length && {
            [CartItemKeys.Products]: childProducts,
          }),
        };
      });

    return this.groupMatchingCartItems(cartItems);
  }

  private mapValidatedAdditionsToCartAdditions(additions: unknown): string[] {
    if (Array.isArray(additions)) {
      return additions.filter((addition): addition is string => typeof addition === 'string');
    }

    if (additions && typeof additions === 'object' && 'products' in additions) {
      const products = (additions as { products?: unknown }).products;
      if (Array.isArray(products)) {
        return products.filter((addition): addition is string => typeof addition === 'string');
      }
    }

    return [];
  }

  private groupMatchingCartItems(cartItems: CartItemWithOrigin[]): CartItemWithOrigin[] {
    const groupedCartItems = cartItems.reduce((acc, cartItem) => {
      const key = this.getCartItemGroupingKey(cartItem);
      const existingCartItem = acc.get(key);

      if (existingCartItem) {
        acc.set(key, {
          ...existingCartItem,
          [CartItemKeys.Quantity]:
            (existingCartItem[CartItemKeys.Quantity] ?? 1) + (cartItem[CartItemKeys.Quantity] ?? 1),
        });
        return acc;
      }

      acc.set(key, cartItem);
      return acc;
    }, new Map<string, CartItemWithOrigin>());

    return Array.from(groupedCartItems.values()).map((cartItem, index) => ({
      ...cartItem,
      [CartItemKeys.Id]: index + 1,
    }));
  }

  private getCartItemGroupingKey(cartItem: CartItemWithOrigin): string {
    return JSON.stringify({
      itemId: cartItem[CartItemKeys.ItemId],
      shopId: cartItem[CartItemKeys.ShopId],
      amount: cartItem[CartItemKeys.Amount],
      additions: this.getSortedAdditions(cartItem[CartItemKeys.Additions]),
      products: this.getSortedCartItemProducts(cartItem[CartItemKeys.Products] ?? []),
      // Paid and free units of the same dish must stay on separate lines.
      originChannel: cartItem[CartItemKeys.Origin]?.channel ?? null,
    });
  }

  private getSortedCartItemProducts(products: ICartDeclinableAdditionsItem[]) {
    return products
      .map(product => ({
        id: product[CartItemKeys.Id],
        shopId: product[CartItemKeys.ShopId],
        storeId: product[CartItemKeys.StoreId],
        quantity: product[CartItemKeys.Quantity],
        additions: this.getSortedAdditions(product[CartItemKeys.Additions] ?? []),
      }))
      .sort((a, b) =>
        [a.storeId, a.id, a.shopId, a.additions.join(',')]
          .join('|')
          .localeCompare([b.storeId, b.id, b.shopId, b.additions.join(',')].join('|')),
      );
  }

  private getSortedAdditions(additions: string[] = []): string[] {
    return [...additions].sort();
  }

  private mapValidatedProductChildrenToCartItems(
    product: ICartValidateItem,
  ): ICartDeclinableAdditionsItem[] {
    if (product[CartItemKeys.Categories]?.length) {
      return product[CartItemKeys.Categories].flatMap(category =>
        category.products.map(childProduct => {
          const separateAdditionsByGroup = this.groupValidatedAdditionsByGroupId(
            childProduct.additions?.items,
          );
          return {
            [CartItemKeys.Id]: childProduct.id,
            [CartItemKeys.ShopId]: childProduct.shop_id,
            [CartItemKeys.Additions]: childProduct.additions?.products ?? [],
            [CartItemKeys.Quantity]: 1,
            ...(separateAdditionsByGroup.length && {
              [CartItemKeys.SeparateAdditionsByGroup]: separateAdditionsByGroup,
            }),
            ...(category.store_id && {
              [CartItemKeys.StoreId]: category.store_id,
            }),
          };
        }),
      );
    }

    if (product[CartItemKeys.Products]?.length) {
      return product[CartItemKeys.Products].map(childProduct => ({
        [CartItemKeys.Id]: childProduct[CartItemKeys.Id],
        [CartItemKeys.ShopId]: childProduct[CartItemKeys.ShopId],
        [CartItemKeys.Additions]: this.mapValidatedAdditionsToCartAdditions(
          childProduct[CartItemKeys.Additions],
        ),
        [CartItemKeys.Quantity]: 1,
      }));
    }

    return [];
  }

  private groupValidatedAdditionsByGroupId(
    items: ValidatedAdditionItem[] = [],
  ): ICartDeclinableSeparateAdditions[] {
    return items.reduce<ICartDeclinableSeparateAdditions[]>((additionsByGroup, item) => {
      if (typeof item.id !== 'string' || typeof item.addition_group !== 'string') {
        return additionsByGroup;
      }

      const existingGroup = additionsByGroup.find(group => group.id === item.addition_group);
      if (existingGroup) {
        existingGroup.selected.push(item.id);
      } else {
        additionsByGroup.push({
          id: item.addition_group,
          selected: [item.id],
        });
      }

      return additionsByGroup;
    }, []);
  }

  private syncCartProductsFromValidation(validation: DoodValidateOrderResponse): void {
    if (!validation[CartKeys.Products]) {
      return;
    }
    const localProducts = this.duplicateCartItem(
      this.cartSelector.active?.[CartKeys.Products] ?? [],
    );
    const validatedProducts = validation[CartKeys.Products].map(product => {
      const localProductIndex = localProducts.findIndex(
        localProduct =>
          localProduct[CartItemKeys.Id] === product[CartItemKeys.Id] &&
          localProduct[CartItemKeys.ShopId] === product[CartItemKeys.ShopId],
      );
      const [localProduct] =
        localProductIndex >= 0 ? localProducts.splice(localProductIndex, 1) : [];

      if (
        product[CartItemKeys.Amount] !== undefined ||
        localProduct?.[CartItemKeys.Amount] === undefined
      ) {
        return product;
      }

      return {
        ...product,
        [CartItemKeys.Amount]: localProduct[CartItemKeys.Amount],
      };
    });
    const nextProducts = this.mapValidatedProductsToCartItems(validatedProducts);
    console.log('nextProducts', nextProducts);
    this.cartDispatcher.updateActive({
      [CartKeys.Products]: nextProducts,
    });
  }

  private notifyIfValidatedProductsChanged(
    sentProducts: ICartValidateItem[],
    nextValidation: DoodValidateOrderResponse,
  ): void {
    const nextProducts = nextValidation[CartKeys.Products];
    const normalizedSentProducts = this.normalizeComparableValidationProducts(sentProducts);
    const normalizedNextProducts = this.normalizeComparableValidationProducts(nextProducts ?? []);
    const productsChanged = this.validationProductsHaveChanged(
      normalizedSentProducts,
      normalizedNextProducts,
    );
    const mismatch = this.getValidationProductsMismatch(
      normalizedSentProducts,
      normalizedNextProducts,
    );

    console.groupCollapsed('[OrdersService] cart adjusted notification check');
    console.log('sentProducts', sentProducts);
    console.log('nextValidationProducts', nextProducts);
    console.log('sentProductsLength', sentProducts.length);
    console.log('hasNextValidationProducts', !!nextProducts);
    console.log('productsChanged', productsChanged);
    console.log('firstMismatch', mismatch);
    console.groupEnd();

    if (sentProducts.length === 0 || !nextProducts || !productsChanged) {
      return;
    }

    console.log('[OrdersService] displaying cart adjusted notification', {
      translationKey: 'basket.notification-cart-adjusted-final-price',
      mismatch,
    });
    this.notificationService.displayMessage(
      this.translateService.instant('basket.notification-cart-adjusted-final-price'),
    );
  }

  private validationProductsHaveChanged(
    sentProducts: ComparableValidationProduct[],
    nextProducts: ComparableValidationProduct[],
  ): boolean {
    return !this.validationProductsAreEqual(sentProducts, nextProducts);
  }

  private normalizeComparableValidationProducts(
    products: ComparableValidationInputProduct[] = [],
  ): ComparableValidationProduct[] {
    return products.flatMap(product => {
      if (typeof product[CartItemKeys.Id] !== 'string') {
        return [];
      }
      const childProducts = this.normalizeComparableValidationProducts(
        this.getComparableValidationChildProducts(product),
      );

      return {
        [CartItemKeys.Id]: product[CartItemKeys.Id],
        ...(typeof product[CartItemKeys.Quantity] === 'number' && {
          [CartItemKeys.Quantity]: product[CartItemKeys.Quantity],
        }),
        ...(childProducts.length && {
          [CartItemKeys.Products]: childProducts,
        }),
      };
    });
  }

  private getComparableValidationChildProducts(
    product: ComparableValidationInputProduct,
  ): ComparableValidationInputProduct[] {
    if (Array.isArray(product[CartItemKeys.Categories])) {
      return product[CartItemKeys.Categories].flatMap(category => {
        if (
          typeof category === 'object' &&
          category &&
          Array.isArray((category as { products?: unknown }).products)
        ) {
          return (category as { products: ComparableValidationInputProduct[] }).products;
        }
        return [];
      });
    }

    if (Array.isArray(product[CartItemKeys.Products])) {
      return product[CartItemKeys.Products] as ComparableValidationInputProduct[];
    }

    return [];
  }

  private validationProductsAreEqual(
    sentProducts: ComparableValidationProduct[] = [],
    nextProducts: ComparableValidationProduct[] = [],
  ): boolean {
    return (
      sentProducts.length === nextProducts.length &&
      sentProducts.every((sentProduct, index) =>
        this.validationProductIsEqual(sentProduct, nextProducts[index]),
      )
    );
  }

  private validationProductIsEqual(
    sentProduct: ComparableValidationProduct,
    nextProduct: ComparableValidationProduct,
  ): boolean {
    return (
      sentProduct[CartItemKeys.Id] === nextProduct[CartItemKeys.Id] &&
      (sentProduct[CartItemKeys.Quantity] ?? 1) === (nextProduct[CartItemKeys.Quantity] ?? 1) &&
      this.validationProductsAreEqual(
        sentProduct[CartItemKeys.Products] ?? [],
        nextProduct[CartItemKeys.Products] ?? [],
      )
    );
  }

  private getValidationProductsMismatch(
    sentProducts: ComparableValidationProduct[] = [],
    nextProducts: ComparableValidationProduct[] = [],
    path: string = CartKeys.Products,
  ): object | null {
    if (sentProducts.length !== nextProducts.length) {
      return {
        path,
        reason: 'length',
        sentValue: sentProducts.length,
        nextValue: nextProducts.length,
      };
    }

    for (const [index, sentProduct] of sentProducts.entries()) {
      const mismatch = this.getValidationProductMismatch(
        sentProduct,
        nextProducts[index],
        `${path}[${index}]`,
      );
      if (mismatch) {
        return mismatch;
      }
    }

    return null;
  }

  private getValidationProductMismatch(
    sentProduct: ComparableValidationProduct,
    nextProduct: ComparableValidationProduct,
    path: string,
  ): object | null {
    if (sentProduct[CartItemKeys.Id] !== nextProduct[CartItemKeys.Id]) {
      return {
        path: `${path}.${CartItemKeys.Id}`,
        reason: CartItemKeys.Id,
        sentValue: sentProduct[CartItemKeys.Id],
        nextValue: nextProduct[CartItemKeys.Id],
      };
    }

    const sentQuantity = sentProduct[CartItemKeys.Quantity] ?? 1;
    const nextQuantity = nextProduct[CartItemKeys.Quantity] ?? 1;
    if (sentQuantity !== nextQuantity) {
      return {
        path: `${path}.${CartItemKeys.Quantity}`,
        reason: CartItemKeys.Quantity,
        sentValue: sentQuantity,
        nextValue: nextQuantity,
      };
    }

    return this.getValidationProductsMismatch(
      sentProduct[CartItemKeys.Products] ?? [],
      nextProduct[CartItemKeys.Products] ?? [],
      `${path}.${CartItemKeys.Products}`,
    );
  }

  private calculateWantedAt(cart: DoodCartModel, _slots?: number[]): Date | null {
    const date = new Date();
    if (DateUtils.dayjsInMarketplaceTimezone(cart[CartKeys.WantedAt]).isAfter(date)) {
      return cart[CartKeys.WantedAt];
    }

    return null;
  }

  mapCartToOrderValues(
    cart: DoodCartModel | null,
    shopSlots?: number[],
    paymentMethod?: string,
    user?: DoodUserModel | null,
    basketId?: string,
    funnelComment?: string | null,
    validatedOrder?: { estimated_delivery_duration?: number | null } | null,
  ): DoodCreateOrderQuery | null {
    if (!cart) {
      return null;
    }

    const cartUser = cart[CartKeys.User];
    const orderType = cart[CartKeys.Type];
    const orderTypeCapabilities = this.orderTypeService.getCapabilities(orderType);
    const kioskId = this.kioskService.getKioskIdFromStore();

    const order: DoodCreateOrderQuery = {
      payment_service: paymentMethod ?? '',
      register_user_to_como: !!cart[CartKeys.RegisterUserToComo],
      type: orderType as DoodOrderType,
      marketplace: cart[CartKeys.Marketplace],
      multi_shop: cart[CartKeys.MultiShop],
      app_platform: cart[CartKeys.AppPlatform] as DoodAppPlatform,
      customer_device: cart[CartKeys.CustomerDevice] as DoodAppDevice,
      use_points: cart[CartKeys.UsePoints] as DoodUsePointModel,
      cart_id: cart[CartKeys.CartId],
      payment_phone_number: cart[CartKeys.PaymentPhoneNumber],
      metafields: [],
      coupons: cart[CartKeys.Coupons] ?? [],
      idempotency_key: generateIdempotencyKey(), // Generate unique key to prevent duplicate orders
      seat_count: this.cartFunnelSelector.selectSeatCount(),
    };

    order.message = [
      cart[CartKeys.Message] ?? null,
      funnelComment ?? null,
      this.hiddenCommentStorageService.getHiddenComment(),
    ]
      .filter(item => !!item)
      .join(' ; ');

    if (cart[CartKeys.Type] === OrderTypeValues.OnSite) {
      order.onsite_location_id = cart[CartKeys.OnSiteLocationId];
    }

    if (!basketId) {
      order.products = this.duplicateCartItem(cart[CartKeys.Products]);
      order.cart_created_at = cart[CartKeys.CartCreatedAt];
    } else {
      order.cart = basketId;
    }

    if (cartUser) {
      order.user = cartUser;
    }

    if (!cart[CartKeys.MultiShop]) {
      order.shop = cart[CartKeys.Shop];
    }

    if (kioskId) {
      order.kiosk = kioskId;
    }

    if (orderTypeCapabilities.isMobilePhoneMandatory) {
      if (cart[CartKeys.ContactPhone]) {
        order.contact_phone_number = cart[CartKeys.ContactPhone];
      } else if (user?.[UserKeys.Phone]) {
        order.contact_phone_number = user[UserKeys.Phone];
      }
    }

    console.log('orderTypeCapabilities', orderTypeCapabilities);
    if (orderTypeCapabilities.preorderingAllowed) {
      order.wanted_at = this.calculateWantedAt(cart, shopSlots);
    } else {
      order.wanted_at = undefined;
    }

    if (!order.wanted_at) {
      order.wanted_at = undefined;
    }

    if (orderType === OrderTypeValues.Delivery && order.wanted_at) {
      const deliveryDurationSeconds =
        validatedOrder?.estimated_delivery_duration ?? DEFAULT_DELIVERY_DURATION_SECONDS;
      order.dropoff_wanted_at = new Date(
        order.wanted_at.getTime() + deliveryDurationSeconds * 1000,
      );
    } else {
      order.dropoff_wanted_at = undefined;
    }

    if (orderTypeCapabilities.isDeliveryAddressMandatory) {
      order.delivery_address = cart[CartKeys.DeliveryAddress] as Partial<DoodAddressModel>;
      if (
        this.deliverySelector.active &&
        !order.delivery_address?.post_code &&
        !order.delivery_address?.street_line_1
      ) {
        order.delivery_address = LocationUtils.mapINewDeliveryAddressToOdDeliveryAddress(
          this.deliverySelector.active,
        ) as DoodAddressModel;
      }
    }

    if (cart[CartKeys.DeliveryAddress]?.instructions) {
      order.delivery_comment = cart[CartKeys.DeliveryAddress]?.instructions;
    }

    if (cart[CartKeys.OnBehalfOf]) {
      order.on_behalf_of = (cart[CartKeys.OnBehalfOf] as DoodOrderUserModel) ?? null;
    }

    if (cart[CartKeys.Instructions]) {
      order.instructions = cart[CartKeys.Instructions];
    }

    const tipAmount = cart?.[CartKeys.Tip];
    if (tipAmount) {
      order.tip = {
        amount: Math.round(tipAmount),
      };
    }

    const utmPayload = this.utmStorageService.getUtmPayload();
    if (utmPayload) {
      order.acquisition_context = {
        platform: 'DOOD',
        ...(utmPayload.utm_source != null && { utm_source: utmPayload.utm_source }),
        ...(utmPayload.utm_medium != null && { utm_medium: utmPayload.utm_medium }),
        ...(utmPayload.utm_campaign != null && { utm_campaign: utmPayload.utm_campaign }),
        metadata: {},
      };
    }

    return order;
  }

  checkCartIsValid$(): Observable<DoodValidateOrderResponse | null> {
    const user = this.authSelector.selectUser();
    if (!user) {
      this.completeValidateOrderLoading();
      return of(null);
    }

    this.cartDispatcher.updateActive({
      ...(user?.id && {
        user: user?.id,
      }),
    });

    // we should get the this.cartSelector.selectActive$ asynchronously to avoid race condition
    // because this.cartDispatcher.updateActive will update the cart after the this.cartSelector.selectActive() call
    return this.cartSelector.selectActive$.pipe(
      filter((cart): cart is DoodCartModel => !!cart && cart.user === user?.id),
      take(1),
      switchMap(cart => {
        const slots = this.shopSelector.selectSlotsSignal();
        const basketId = this.basketSelector.selectIdSignal();
        const funnelComment = this.cartFunnelSelector.selectOrderCommentSignal();
        const activeValidation = this.orderStoreSelector.selectValidation();

        const order = this.mapCartToOrderValues(
          cart,
          slots,
          undefined,
          user,
          basketId,
          funnelComment,
          activeValidation ?? undefined,
        );
        // If the last item is removed from the cart, the order is empty but it keeps a final price it must be reset
        // Because, an empty cart will not trigger the validation (and if the validation is trigger, it will display an error that indicate something like the cart must not be empty...)
        const productCount = order?.products?.length;
        const emptyCartUnSync = !productCount && activeValidation?.final_price !== 0;
        if (emptyCartUnSync) {
          this.orderDispatcher.resetValidation();
          this.orderDispatcher.resetErrors();
        }

        const basket = this.basketSelector.basketSignal();
        if (basket?.[CartKeys.Id] && basket[CartKeys.CartItems]?.length < 1) {
          this.resetValidateOrderState();
          return of(null);
        }
        if (!basket?.[CartKeys.Id] && (!productCount || productCount < 1)) {
          this.resetValidateOrderState();
          return of(null);
        }
        if (!order || !order.user) {
          this.completeValidateOrderLoading();
          return of(null);
        }

        const requestKey = this.buildValidateOrderRequestKey(order);
        const cachedResponse = this.getCachedValidateOrderResponse(requestKey);
        const hasInFlightRequest = this.validateOrderInFlightByKey.has(requestKey);

        if (cachedResponse) {
          return of(cachedResponse);
        }

        if (!hasInFlightRequest) {
          this.checkCartIsValidCallInProgress$.next(true);
          this.orderDispatcher.updateIsValidating(true);
        }

        const sentProducts = order[CartKeys.Products] ?? [];
        return this.validateOrderWithCache$(order, requestKey).pipe(
          tap(validation => {
            this.notifyIfValidatedProductsChanged(sentProducts, validation);
          }),
        );
      }),
      tap(validation => {
        if (validation) {
          this.applyValidateOrderResult(validation);
        } else {
          this.completeValidateOrderLoading();
        }
      }),
      catchError(error => {
        this.clearValidateOrderCache();
        this.completeValidateOrderLoading();
        this.orderDispatcher.updateErrors(error.error);
        this.orderDispatcher.resetValidation();

        const violations = error.error?.violations ?? [];
        const unavailableIds = this.getUnavailableProductIdsFromViolations(violations);
        const basket = this.basketSelector.basket;
        if (
          unavailableIds.length === 0 ||
          basket?.[CartKeys.Id] ||
          !this.cartSelector.active?.[CartKeys.Products]?.length
        ) {
          return throwError(() => error);
        }

        return this.cartSelector.selectActive$.pipe(
          take(1),
          switchMap(cart => {
            const kept = (cart?.[CartKeys.Products] ?? []).filter(
              item => !this.isCartItemUnavailable(item, unavailableIds),
            );
            const hasRemoveUnavailableProducts =
              kept.length !== (cart?.[CartKeys.Products] ?? []).length;
            if (hasRemoveUnavailableProducts) {
              const removed = (cart?.[CartKeys.Products] ?? []).filter(item =>
                this.isCartItemUnavailable(item, unavailableIds),
              );
              // The follow-up validate drops the violation. Show the modal now and let that call run.
              this.presentRemovedUnavailableProducts(violations, removed);
              this.cartDispatcher.updateActive({ products: kept });
              return this.orderDispatcher.checkCartIsValid$();
            }
            return throwError(() => error);
          }),
        );
      }),
      tap(cart => this.setCartId(cart)),
      finalize(() => this.completeValidateOrderLoading()),
    );
  }

  checkAddressIsValid$(type?: string, location?: IValidateDeliveryAddress): Observable<boolean> {
    const marketplace = this.marketplaceSelector.marketplace;
    const shop = this.shopSelector.active;
    const isMultiShop = marketplace[MarketplaceKeys.CartScope] === 'MARKETPLACE';

    const cart = this.cartSelector.active;
    const deliveryAddress = location || cart?.[CartKeys.DeliveryAddress];
    const body = {
      type: type ?? cart?.type,
      multi_shop: isMultiShop,
      marketplace: marketplace.id,
      ...(!isMultiShop && {
        shop: shop?.id,
      }),
      delivery_address: deliveryAddress,
    };

    const requestKey = this.buildValidateAddressRequestKey(body);
    const cached = this.getCachedValidateAddressResponse(requestKey);
    if (cached !== null) {
      return of(cached);
    }

    const existingInFlight = this.validateAddressInFlightByKey.get(requestKey);
    if (existingInFlight) {
      return existingInFlight;
    }

    const request$ = defer(() => this.ordersApiService.validateAddress$(body)).pipe(
      tap(result => {
        this.trackSearchDeliveryAddress(deliveryAddress, result);
        this.cacheValidateAddressResponse(requestKey, result?.is_address_valid === true);
      }),
      map(result => result?.is_address_valid === true),
      catchError(error => {
        this.trackSearchDeliveryAddress(deliveryAddress, { is_address_valid: false });
        return throwError(() => error);
      }),
      finalize(() => this.validateAddressInFlightByKey.delete(requestKey)),
      shareReplay({ bufferSize: 1, refCount: false }),
    );

    this.validateAddressInFlightByKey.set(requestKey, request$);
    return request$;
  }

  private trackSearchDeliveryAddress(
    deliveryAddress?: IValidateDeliveryAddress,
    result?: { is_address_valid: boolean },
  ): void {
    this.analyticsService.trackSearchDeliveryAddress(
      deliveryAddress?.point?.coordinates?.[1],
      deliveryAddress?.point?.coordinates?.[0],
      JSON.stringify(deliveryAddress),
      result?.is_address_valid === true,
    );
  }

  createOrder$(payment: string): Observable<DoodOrderModel | null> {
    return combineLatest([
      this.cart$,
      this.shopSelector.selectSlots,
      this.authSelector.selectUser$,
      this.basketSelector.selectId,
      this.cartFunnelSelector.selectOrderComment,
      this.orderStoreSelector.selectValidation$,
    ]).pipe(
      take(1),
      map(([cart, slots, user, basketId, funnelComment, validation]) =>
        this.alignOrderUserWithSession(
          this.mapCartToOrderValues(
            cart,
            slots,
            payment,
            user,
            basketId,
            funnelComment,
            validation ?? undefined,
          ),
          user,
        ),
      ),
      tap(cart => this.setCartId(cart)),
      switchMap(cart =>
        cart
          ? this.ordersApiService
              .createOrder$(cart)
              .pipe(tap({ error: error => this.logOrderAuthorizationError(error, cart) }))
          : of(null),
      ),
      retryWhen(err => {
        return err.pipe(
          mergeMap((error, attempt) => {
            if (attempt > 5) {
              return throwError(() => 'An error occurred.');
            }
            if (error?.error?.error === 'POS_ERROR') {
              return throwError(() => error);
            }
            // Cart / product input errors won't resolve by retrying
            if (error?.error?.error === 'INPUT_DATA_ERRORS') {
              return throwError(() => error);
            }
            if (
              this.containsAnyError(
                ['ANONYMOUS_ORDER_NOT_ALLOWED', 'MUST_BE_LOGGED_IN'],
                error?.error?.violations,
              )
            ) {
              console.warn('Not logged in - disconnect and restart');
              this.resetStateService.refreshUI().subscribe(() => {
                window.location.reload();
              });
              return throwError(() => 'Not logged in');
            }
            return this.checkCartIsValid$().pipe(delay(5000));
          }),
        );
      }),
      tap(order => {
        this.applyAnonymousAccessTokenFromOrder(order);
        if (order) {
          this.eventTrackerService.dispatch(EventTrackerService.EventOrderCreated, order);
        }
      }),
      catchError(error => {
        const apiError = error?.error;
        if (apiError && typeof apiError === 'object') {
          // Same shape as validateOrder errors so ErrorService can map violations
          this.orderDispatcher.updateErrors(apiError);
        } else {
          this.orderDispatcher.updateErrors({ error });
        }
        this.orderDispatcher.resetValidation();
        if (apiError?.error === 'POS_ERROR') {
          const message = this.translateService.instant('errors.pos-error', {
            detail: apiError.detail ?? '',
          });
          this.modalsService.open(AlertModalComponent.handle);
          this.modalsService.setData(AlertModalComponent.handle, { message });
        } else if (this.containsAnyError(ErrorService.SLOT_ERRORS, apiError?.violations)) {
          const wantedAt = this.cartSelector.active?.wanted_at;
          if (wantedAt) {
            this.modalsService.open(CartParametersModalComponent.handle);
          }
        } else if (apiError?.error === 'INPUT_DATA_ERRORS') {
          this.removeUnavailableProductsFromCart(apiError.violations ?? []);
          this.linkNavigationService.navigate(`/${Paths.Cart}`);
        }
        return throwError(() => error);
      }),
    );
  }

  loadOrderById$(id: string, shareCode?: string): Observable<DoodOrderModel> {
    return this.ordersApiService.getOrderById$(id, shareCode).pipe(
      tap(order => {
        this.applyAnonymousAccessTokenFromOrder(order);
        this.orderDispatcher.upsertMany([order, ...(order.child_orders ?? [])]);
      }),
    );
  }

  /**
   * Injects a mock order into the store for e2e testing.
   * Same behavior as loadOrderById$ but without API call.
   * Overrides order.id with routeOrderId so blocks can find it by route param.
   */
  injectMockOrder(order: DoodOrderModel, routeOrderId: string): void {
    const orderWithRouteId = { ...order, id: routeOrderId };
    this.orderDispatcher.upsertMany([orderWithRouteId, ...(order.child_orders ?? [])]);
    this.setActiveOrderToStore(orderWithRouteId);
    this.orderDispatcher.updateIsLoading(false);
  }

  getPrintedTicketById$(id: string, charByLine = 47): Observable<string[]> {
    return this.ordersApiService.getPrintedTicketById$(id, charByLine);
  }

  setActiveOrderToStore(order: DoodOrderModel): void {
    this.orderDispatcher.upsertOne(order);
    this.orderDispatcher.setActive(order.id);
  }

  loadShopSlots$(): Observable<number[]> {
    return combineLatest([
      this.shopSelector.selectActive$,
      this.marketplaceSelector.selectMarketplace$,
      this.cart$,
    ]).pipe(
      // Coalesce bootstrap emissions (shop hydration, cart metadata sync) before resolving the cart
      debounceTime(100),
      map(([shop, marketplace, cartActive]) => ({
        shop,
        marketplace,
        cartActive,
        triggerKey: this.buildLoadSlotsTriggerKey(shop, marketplace, cartActive),
      })),
      distinctUntilChanged((previous, current) => previous.triggerKey === current.triggerKey),
      switchMap(({ shop, marketplace, cartActive }) => {
        // TODO: Replace by enums and type definitions
        if (marketplace.cart_scope === 'MARKETPLACE' || shop) {
          return this.cartSelector
            .selectCart(
              marketplace.cart_scope === 'MARKETPLACE'
                ? `marketplace_${marketplace[MarketplaceKeys.Id]}`
                : `shop_${shop?.[ShopKeys.Id]}`,
            )
            .pipe(take(1));
        }

        return of(cartActive);
      }),
      distinctUntilChanged(
        (previous, current) =>
          this.buildSlotsRequestKey(previous) === this.buildSlotsRequestKey(current),
      ),
      exhaustMap(cart => {
        if (!cart) {
          return of([]);
        }
        return this.getOrderSlots$(cart, cart.wanted_at);
      }),
      tap(timeSlots => {
        this.shopDispatcher.setSlots(timeSlots ?? []);
      }),
    );
  }

  private buildLoadSlotsTriggerKey(
    shop: DoodShopModel | null | undefined,
    marketplace: { [MarketplaceKeys.Id]: string; cart_scope: string },
    cart: DoodCartModel | null | undefined,
  ): string {
    return JSON.stringify({
      shopId: shop?.[ShopKeys.Id] ?? null,
      marketplaceId: marketplace[MarketplaceKeys.Id],
      cartScope: marketplace.cart_scope,
      slotsKey: this.buildSlotsRequestKey(cart),
    });
  }

  private completeValidateOrderLoading(): void {
    this.checkCartIsValidCallInProgress$.next(false);
    this.orderDispatcher.updateIsValidating(false);
  }

  private applyValidateOrderResult(validation: DoodValidateOrderResponse): void {
    this.orderDispatcher.resetErrors();
    this.syncCartProductsFromValidation(validation);
    this.orderDispatcher.updateValidation(validation);
    this.completeValidateOrderLoading();
  }

  private resetValidateOrderState(): void {
    this.orderDispatcher.resetValidation();
    this.orderDispatcher.resetErrors();
    this.completeValidateOrderLoading();
  }

  private buildValidateOrderRequestKey(order: DoodValidateOrderQuery): string {
    const {
      // Server-assigned cart metadata must not affect cache identity.
      cart_id: _cartId,
      cart_created_at: _cartCreatedAt,
      // Generated per validate call; would otherwise make every cache lookup a miss.
      idempotency_key: _idempotencyKey,
      ...payload
    } = order as DoodCreateOrderQuery;
    return JSON.stringify(payload);
  }

  private getCachedValidateOrderResponse(requestKey: string): DoodValidateOrderResponse | null {
    const cache = this.validateOrderCache;
    if (!cache || cache.requestKey !== requestKey) {
      return null;
    }

    if (Date.now() - cache.cachedAt > VALIDATE_ORDER_CACHE_TTL_MS) {
      return null;
    }

    return cache.response;
  }

  private cacheValidateOrderResponse(
    requestKey: string,
    response: DoodValidateOrderResponse,
  ): void {
    this.validateOrderCache = {
      requestKey,
      cachedAt: Date.now(),
      response,
    };
  }

  private clearValidateOrderCache(): void {
    this.validateOrderCache = null;
    this.validateOrderInFlightByKey.clear();
  }

  private buildValidateAddressRequestKey(body: {
    type?: string;
    multi_shop: boolean;
    marketplace: string;
    shop?: string;
    delivery_address?: IValidateDeliveryAddress;
  }): string {
    const deliveryAddress = body.delivery_address;
    // Instructions / addressee do not affect deliverability; exclude them from cache identity.
    const addressKey = deliveryAddress
      ? {
          street_line_1: deliveryAddress.street_line_1,
          post_code: deliveryAddress.post_code,
          city: deliveryAddress.city,
          country: deliveryAddress.country,
          point: deliveryAddress.point,
        }
      : null;

    return JSON.stringify({
      type: body.type,
      multi_shop: body.multi_shop,
      marketplace: body.marketplace,
      shop: body.shop ?? null,
      delivery_address: addressKey,
    });
  }

  private getCachedValidateAddressResponse(requestKey: string): boolean | null {
    const cache = this.validateAddressCache;
    if (!cache || cache.requestKey !== requestKey) {
      return null;
    }

    if (Date.now() - cache.cachedAt > VALIDATE_ADDRESS_CACHE_TTL_MS) {
      return null;
    }

    return cache.isValid;
  }

  private cacheValidateAddressResponse(requestKey: string, isValid: boolean): void {
    this.validateAddressCache = {
      requestKey,
      cachedAt: Date.now(),
      isValid,
    };
  }

  private validateOrderWithCache$(
    order: DoodValidateOrderQuery,
    requestKey: string,
  ): Observable<DoodValidateOrderResponse> {
    const cachedResponse = this.getCachedValidateOrderResponse(requestKey);
    if (cachedResponse) {
      return of(cachedResponse);
    }

    const existingInFlight = this.validateOrderInFlightByKey.get(requestKey);
    if (existingInFlight) {
      return existingInFlight;
    }

    const request$ = defer(() => this.ordersApiService.validateOrder$(order)).pipe(
      tap(response => this.cacheValidateOrderResponse(requestKey, response)),
      finalize(() => this.validateOrderInFlightByKey.delete(requestKey)),
      shareReplay({ bufferSize: 1, refCount: false }),
    );

    this.validateOrderInFlightByKey.set(requestKey, request$);
    return request$;
  }

  private buildSlotsRequestKey(cart: DoodCartModel | null | undefined): string {
    if (!cart) {
      return '';
    }

    const wantedAt = cart[CartKeys.WantedAt];
    const wantedAtDayUnix = wantedAt
      ? DateUtils.dayjsInMarketplaceTimezone(wantedAt).startOf('day').unix()
      : DateUtils.dayjsInMarketplaceTimezone().startOf('day').unix();

    return JSON.stringify({
      shop: cart[CartKeys.Shop],
      type: cart[CartKeys.Type],
      wantedAtDayUnix,
      multiShop: cart[CartKeys.MultiShop],
      onSiteLocationId: cart[CartKeys.OnSiteLocationId],
      products: (cart[CartKeys.Products] ?? []).map(item => ({
        id: item[CartItemKeys.ItemId],
        qty: item[CartItemKeys.Quantity],
        shopId: item[CartItemKeys.ShopId],
      })),
      deliveryAddress: cart[CartKeys.DeliveryAddress],
    });
  }

  getOrderSlots$(
    cart: DoodCartModel | null,
    wantedAtDate?: Date | null,
    distributionMode?: string,
  ): Observable<number[]> {
    if (!cart) {
      return of([]);
    }

    if (!wantedAtDate) {
      wantedAtDate = new Date();
    }

    if (distributionMode) {
      cart = {
        ...cart,
        [CartKeys.Type]: distributionMode,
      };
    }

    wantedAtDate = new Date(
      DateUtils.dayjsInMarketplaceTimezone(wantedAtDate).startOf('day').format(),
    );
    const wantedAtDateUnix = Math.max(
      DateUtils.dayjsInMarketplaceTimezone(wantedAtDate).unix(),
      DateUtils.dayjsInMarketplaceTimezone().unix(),
    );

    const order = {
      time: wantedAtDateUnix,
      order: this.mapCartToOrderValues(cart),
    };

    if (!order.order) {
      return of([]);
    }
    return this.ordersApiService.getOrdersSlots$(order);
  }

  clearOrders(): void {
    this.deActiveOrderToStore();
    this.orderDispatcher.removeAll();
    this.orderDispatcher.clear();
  }

  removeOrderFromStore(id: string): void {
    this.orderDispatcher.removeOne(id);
  }

  deActiveOrderToStore(): void {
    this.orderDispatcher.resetActive();
  }

  setLoadingToStore(): void {
    this.orderDispatcher.updateIsLoading(true);
  }

  setDefaultPhone(): void {
    this.orderDispatcher.updateValidation(null);
    this.orderDispatcher.updateErrors({
      missing_number: true,
    });
  }

  removeDefaultPhone(): void {
    this.orderDispatcher.updateValidation(null);
    this.orderDispatcher.updateErrors({
      missing_number: false,
    });
  }

  getOrderById$(id: string): Observable<DoodOrderModel> {
    return this.ordersApiService.getOrderById$(id);
  }

  getActiveOrders$(userId: string, marketplaceId: string): Observable<DoodOrderModel[]> {
    return this.ordersApiService.getActiveOrders$(userId, marketplaceId).pipe(
      map(orders => orders.filter(order => this.isActiveOrder(order))),
      map(orders => this.unwindMultiBasketOrders(orders)),
    );
  }

  getHistoricalOrders$(userId: string, marketplaceId: string): Observable<DoodOrderModel[]> {
    return this.ordersApiService.getHistoricalOrders$(userId, marketplaceId).pipe(
      map(orders => orders.filter(order => this.isHistoricalOrder(order))),
      map(orders => this.unwindMultiBasketOrders(orders)),
    );
  }

  getOrdersTransactions$(id: string): Observable<DoodTransactionModel[]> {
    return this.ordersApiService
      .getOrdersTransactions$(id)
      .pipe(tap(transactions => this.transactionDispatcher.upsertMany(transactions)));
  }

  removeOrderErrors(): void {
    this.orderDispatcher.resetErrors();
  }

  mapIDeliveryAddress(address: Partial<DoodDeliveryAddressModel>): IValidateDeliveryAddress {
    return {
      street_line_1: address.street ?? '',
      point: address.point,
      city: address.city ?? '',
      post_code: address.postal_code ?? '',
      country: address.country ?? '',
      instructions: address.instructions ?? '',
    };
  }

  private unwindMultiBasketOrders(orders: DoodOrderModel[]): DoodOrderModel[] {
    const singleBasketOrders: DoodOrderModel[] = [];
    for (const mainOrder of orders) {
      if (mainOrder.multi_shop) {
        singleBasketOrders.push(...(mainOrder.child_orders ?? []));
      } else {
        singleBasketOrders.push(mainOrder);
      }
    }
    return singleBasketOrders;
  }

  private setCartId(cart: DoodValidateOrderQuery | IOrderValidate | null): void {
    if (!cart || !cart[CartKeys.CartCreatedAt] || !cart[CartKeys.CartId]) {
      return;
    }

    this.cartDispatcher.updateActive({
      cart_id: cart.cart_id,
      cart_created_at: cart.cart_created_at,
    });
  }

  private isActiveOrder(order: DoodOrderModel): boolean {
    const notActiveOrderStatus = [OrderStatusValues.Payment, OrderStatusValues.Cancelled];
    return !(notActiveOrderStatus as string[]).includes(order.status);
  }

  private isHistoricalOrder(order: DoodOrderModel): boolean {
    return order.status !== DoodOrderStatus.cancelled;
  }

  placeResultToDeliveryAddress(place?: PlaceResult): IValidateDeliveryAddress | undefined {
    if (!place) {
      return undefined;
    }

    const deliveryAddress: IValidateDeliveryAddress = {
      street_line_1: '',
      city: '',
      post_code: '',
      country: '',
      point: {
        coordinates: [
          (typeof place?.geometry?.location.lng === 'function'
            ? place?.geometry?.location.lng()
            : place.lng) as number,
          (typeof place?.geometry?.location.lat === 'function'
            ? place?.geometry?.location.lat()
            : place.lat) as number,
        ],
      },
      instructions: null,
    };

    // Process address components by checking all types, not just the first one
    place.address_components?.forEach((el: GeocoderAddressComponent) => {
      // Check if any of the types match our criteria
      const hasType = (type: string) => el.types.includes(type);

      if (hasType('plus_code')) {
        deliveryAddress.street_line_1 = deliveryAddress.street_line_1 + ' ' + el.long_name;
      } else if (hasType('street_number')) {
        deliveryAddress.street_line_1 = deliveryAddress.street_line_1 + ' ' + el.long_name;
      } else if (hasType('route')) {
        deliveryAddress.street_line_1 = deliveryAddress.street_line_1 + ' ' + el.long_name;
      } else if (hasType('locality') || hasType('sublocality')) {
        // Use locality or sublocality for city
        if (!deliveryAddress.city) {
          deliveryAddress.city = el.long_name;
        }
      } else if (hasType('postal_code')) {
        deliveryAddress.post_code = el.long_name;
      } else if (hasType('country')) {
        deliveryAddress.country = el.short_name;
      }
    });

    deliveryAddress.street_line_1 = deliveryAddress.street_line_1.trim();

    return deliveryAddress;
  }

  // TODO Why not add 'code' property to IViolations instead of using type 'any' and risk not finding the 'code' property ?
  containsAnyError(errorCodes: string[], violations: IViolations[]): boolean {
    return (violations ?? []).some((violation: any) => {
      return errorCodes.includes(violation.code);
    });
  }

  private static readonly UNAVAILABLE_PRODUCT_VIOLATION_CODES: string[] = [
    'PRODUCT_NOT_AVAILABLE_FOR_DISTRIBUTION_MODE',
    'PRODUCT_HIDDEN',
    'PRODUCT_NOT_AVAILABLE',
  ];

  private getUnavailableProductIdsFromViolations(violations: DoodOrderViolationModel[]): string[] {
    if (!violations?.length) return [];
    const ids = violations
      .filter(v => v.code && OrdersService.UNAVAILABLE_PRODUCT_VIOLATION_CODES.includes(v.code))
      .map(v => v.data?.id as string)
      .filter(Boolean);
    return [...new Set(ids)];
  }

  private removeUnavailableProductsFromCart(violations: DoodOrderViolationModel[]): void {
    const unavailableIds = this.getUnavailableProductIdsFromViolations(violations);
    const basket = this.basketSelector.basket;
    const cartProducts = this.cartSelector.active?.[CartKeys.Products] ?? [];
    if (unavailableIds.length === 0 || basket?.[CartKeys.Id] || !cartProducts.length) {
      return;
    }

    const kept = cartProducts.filter(item => !this.isCartItemUnavailable(item, unavailableIds));
    if (kept.length !== cartProducts.length) {
      const removed = cartProducts.filter(item => this.isCartItemUnavailable(item, unavailableIds));
      this.presentRemovedUnavailableProducts(violations, removed);
      this.cartDispatcher.updateActive({ products: kept });
    }
  }

  private presentRemovedUnavailableProducts(
    violations: DoodOrderViolationModel[],
    removedItems: ICartItem[],
  ): void {
    const products = this.buildUnavailableProductCards(violations, removedItems);
    if (!products.length) {
      return;
    }

    this.modalsService.open<UnavailableProductModalConfig>('unavailable-product-modal', undefined, {
      products,
    });

    const hasOtherViolations = violations.some(
      violation =>
        !violation.code ||
        !OrdersService.UNAVAILABLE_PRODUCT_VIOLATION_CODES.includes(violation.code),
    );
    if (!hasOtherViolations) {
      this.orderDispatcher.resetErrors();
    }
  }

  private buildUnavailableProductCards(
    violations: DoodOrderViolationModel[],
    removedItems: ICartItem[],
  ): UnavailableProductCard[] {
    return this.getUnavailableProductIdsFromViolations(violations)
      .filter(id => removedItems.some(item => this.isCartItemUnavailable(item, [id])))
      .map(id => {
        const violation = violations.find(entry => entry.data?.id === id);
        const name = (violation?.data?.name as string | undefined) || '';

        return {
          id,
          name,
        };
      })
      .filter(product => product.name);
  }

  private isCartItemUnavailable(item: ICartItem, unavailableProductIds: string[]): boolean {
    if (unavailableProductIds.includes(item[CartItemKeys.ItemId])) return true;
    const products = item[CartItemKeys.Products];
    if (!products?.length) return false;
    return products.some(
      sub =>
        unavailableProductIds.includes(sub[CartItemKeys.Id]) ||
        sub[CartItemKeys.Additions]?.some((addId: string) => unavailableProductIds.includes(addId)),
    );
  }
}
